<!--
  PHASE 5B + Sprint 5.0.5.1 (2026-05-16): Disparo manual de bot na sidebar.

  Sprint 5.0.5.1 — redesign UX:
    - Combobox custom estilo Kinbox (substitui <select> HTML)
    - Input de busca no popover
    - Lista vertical com bolinha colorida (bot.color) + nome
    - Status dots discretos (cinza inativo, ambar outro inbox) + tooltip
    - Hint inline minimo (1 linha) quando bot selecionado precisa atencao
    - console.log/error defensivo na funcao trigger pra debug futuro

  Endpoints usados (BotsAPI):
    - getBotFlowTriggers(displayId) — lista bots da conta + sessao ativa
    - triggerBot(botId, displayId) — POST /bmt_bots/:id/trigger
    - cancelBotSession(sessionId) — DELETE /bmt_bot_sessions/:id

  Renderizado em ContactPanel.vue acima da lista Draggable de secoes.
-->
<template>
  <div class="bot-flow-trigger px-3 py-3 border-b border-slate-100 dark:border-slate-800">
    <div class="flex items-center justify-between mb-2">
      <h4 class="text-[12px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Disparar bot
      </h4>
      <button
        v-if="!loading"
        class="text-slate-400 hover:text-slate-600 transition-colors"
        title="Recarregar"
        @click="load"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25">
          <polyline points="23 4 23 10 17 10"/>
          <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
        </svg>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center gap-2 text-[12px] text-slate-400 py-2">
      <span class="w-3 h-3 border-2 border-slate-200 border-t-indigo-500 rounded-full animate-spin"></span>
      Carregando...
    </div>

    <!-- Erro -->
    <div v-else-if="errorMsg" class="text-[12px] text-red-600 dark:text-red-400 py-1">
      {{ errorMsg }}
      <button class="ml-2 underline hover:no-underline" @click="load">retry</button>
    </div>

    <!-- Sessão rodando -->
    <div
      v-else-if="runningSession"
      class="flex items-start gap-3 p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800"
    >
      <div
        class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white text-[11px] font-bold"
        :style="{ background: runningSession.bot_color || '#8B5CF6' }"
      >
        BOT
      </div>

      <div class="min-w-0 flex-1">
        <div class="text-[11px] text-violet-700/80 dark:text-violet-300/80 uppercase font-medium">
          Em atendimento pelo bot
        </div>
        <div class="text-[14px] font-semibold text-violet-900 dark:text-violet-100 truncate">
          {{ runningSession.bot_name }}
        </div>
        <div
          v-if="runningSession.triggered_by_user_name"
          class="text-[11px] text-violet-700/70 dark:text-violet-300/70 mt-0.5 truncate"
        >
          Disparado por {{ runningSession.triggered_by_user_name }}
        </div>
        <div class="flex items-center gap-1.5 mt-1">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span class="text-[10px] text-violet-600/70 dark:text-violet-300/70">Executando...</span>
        </div>
      </div>

      <button
        class="flex-shrink-0 px-2.5 py-1 text-[11px] font-medium text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors disabled:opacity-50"
        :disabled="canceling"
        @click="cancelSession"
      >
        {{ canceling ? '...' : 'Cancelar' }}
      </button>
    </div>

    <!-- Vazio -->
    <p v-else-if="bots.length === 0" class="text-[12px] text-slate-400 italic py-1">
      Nenhum bot cadastrado na conta.
    </p>

    <!-- Combobox Kinbox-style -->
    <div v-else class="space-y-2">
      <div ref="comboboxRef" class="relative">
        <button
          type="button"
          class="combobox-trigger"
          :class="{ 'is-open': open }"
          @click="open = !open"
        >
          <span v-if="selectedBot" class="flex items-center gap-2 min-w-0 flex-1">
            <span class="bot-dot" :style="{ background: selectedBot.color || '#8B5CF6' }" />
            <span class="truncate">{{ selectedBot.name }}</span>
          </span>
          <span v-else class="text-slate-400 flex-1 text-left">Selecione um bot...</span>
          <svg class="chevron" :class="{ rotated: open }" width="12" height="12" viewBox="0 0 12 12">
            <path d="M3 4.5l3 3 3-3" stroke="currentColor" stroke-width="1.5" fill="none" />
          </svg>
        </button>

        <Transition name="fade-fast">
          <div v-if="open" class="combobox-popover" @click.stop>
            <input
              ref="searchInputRef"
              v-model="searchTerm"
              type="text"
              class="combobox-search"
              placeholder="Pesquisar bot..."
              @keydown.escape="open = false"
            />
            <ul class="combobox-list">
              <li
                v-for="bot in filteredBots"
                :key="bot.id"
                class="combobox-item"
                :class="{ 'is-selected': bot.id === selectedBotId }"
                @click="selectBot(bot)"
              >
                <span class="bot-dot" :style="{ background: bot.color || '#8B5CF6' }" />
                <span class="bot-name truncate">{{ bot.name }}</span>
                <span class="status-dots">
                  <span
                    v-if="!bot.active"
                    class="status-dot inactive"
                    title="Bot inativo — auto-dispatch desligado, mas disparo manual funciona"
                  />
                  <span
                    v-if="!bot.in_current_inbox"
                    class="status-dot other-inbox"
                    :title="`Configurado para: ${bot.primary_inbox_names?.length ? bot.primary_inbox_names.join(', ') : 'sem inbox'}`"
                  />
                </span>
              </li>
              <li v-if="filteredBots.length === 0" class="combobox-empty">
                Nenhum bot encontrado
              </li>
            </ul>
            <div class="combobox-legend">
              <span class="legend-item">
                <span class="status-dot inactive" />
                Inativo
              </span>
              <span class="legend-item">
                <span class="status-dot other-inbox" />
                Outro inbox
              </span>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Hint inline discreto (1 linha) -->
      <p
        v-if="selectedBot && (selectedBotIsInactive || selectedBotIsOtherInbox)"
        class="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1.5 px-1"
      >
        <span v-if="selectedBotIsInactive" class="status-dot inactive inline-flex" />
        <span v-if="selectedBotIsInactive">Inativo</span>
        <span v-if="selectedBotIsInactive && selectedBotIsOtherInbox" class="text-slate-300">·</span>
        <span v-if="selectedBotIsOtherInbox" class="status-dot other-inbox inline-flex" />
        <span v-if="selectedBotIsOtherInbox" class="truncate">
          Inbox: {{ selectedBot.primary_inbox_names?.length ? selectedBot.primary_inbox_names.join(', ') : 'sem inbox' }}
        </span>
      </p>

      <button
        :disabled="!selectedBotId || triggering"
        class="trigger-btn"
        :class="{ 'has-bot': selectedBotId }"
        @click="trigger"
      >
        {{ triggering ? 'Disparando...' : 'Disparar' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import BotsAPI from 'dashboard/api/bots';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  conversationDisplayId: { type: [String, Number], required: true },
});

const emit = defineEmits(['triggered', 'canceled']);

const loading = ref(false);
const triggering = ref(false);
const canceling = ref(false);
const errorMsg = ref('');
const bots = ref([]);
const runningSession = ref(null);
const selectedBotId = ref(null);

// Combobox state
const open = ref(false);
const searchTerm = ref('');
const searchInputRef = ref(null);
const comboboxRef = ref(null);

const selectedBot = computed(() => {
  if (!selectedBotId.value) return null;
  return bots.value.find(b => b.id === selectedBotId.value);
});

const selectedBotIsInactive = computed(() => selectedBot.value && !selectedBot.value.active);
const selectedBotIsOtherInbox = computed(() => selectedBot.value && !selectedBot.value.in_current_inbox);

const filteredBots = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return bots.value;
  return bots.value.filter(b => (b.name || '').toLowerCase().includes(term));
});

const selectBot = (bot) => {
  selectedBotId.value = bot.id;
  open.value = false;
};

watch(open, async (isOpen) => {
  if (isOpen) {
    await nextTick();
    searchInputRef.value?.focus();
  } else {
    searchTerm.value = '';
  }
});

const handleClickOutside = (e) => {
  if (open.value && comboboxRef.value && !comboboxRef.value.contains(e.target)) {
    open.value = false;
  }
};

// Sprint 5.0.5.3: polling automatico enquanto bot esta executando.
// BotTriggerJob roda async via Sidekiq (~1-3s); load() logo apos POST trigger
// pega session ainda :active. Sem polling, runningSession do state local nunca
// mais atualiza ate refresh manual. Polling 3s reflete transicao para null
// quando backend ve session :completed/:expired.
let pollInterval = null;
const POLL_INTERVAL_MS = 3000;

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
};

const startPolling = () => {
  if (pollInterval) return;
  pollInterval = setInterval(() => {
    if (!runningSession.value) {
      stopPolling();
      return;
    }
    load();
  }, POLL_INTERVAL_MS);
};

// Watch runningSession: inicia poll quando vira ativo, para quando vira null
watch(runningSession, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    startPolling();
  } else if (!newVal && oldVal) {
    stopPolling();
  }
});

const load = async () => {
  if (!props.conversationDisplayId) return;
  loading.value = true;
  errorMsg.value = '';
  try {
    const { data } = await BotsAPI.getBotFlowTriggers(props.conversationDisplayId);
    bots.value = data.bots || [];
    runningSession.value = data.running_session || null;
    if (!bots.value.find(b => b.id === selectedBotId.value)) {
      selectedBotId.value = null;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[BotFlowTrigger] load erro:', e);
    errorMsg.value = e?.response?.data?.error || e?.message || 'Erro ao carregar bots';
  } finally {
    loading.value = false;
  }
};

const trigger = async () => {
  if (!selectedBotId.value) return;
  triggering.value = true;
  errorMsg.value = '';
  try {
    const response = await BotsAPI.triggerBot(selectedBotId.value, props.conversationDisplayId);
    // eslint-disable-next-line no-console
    console.log('[BotFlowTrigger] trigger OK:', response.data);
    emit('triggered', selectedBotId.value);
    await load();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[BotFlowTrigger] trigger error:', e?.response || e);
    errorMsg.value = e?.response?.data?.error || e?.message || 'Erro ao disparar bot';
  } finally {
    triggering.value = false;
  }
};

const cancelSession = async () => {
  if (!runningSession.value) return;
  if (!window.confirm('Cancelar a execução do bot nesta conversa?')) return;
  canceling.value = true;
  errorMsg.value = '';
  try {
    await BotsAPI.cancelBotSession(runningSession.value.id);
    emit('canceled', runningSession.value.id);
    await load();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[BotFlowTrigger] cancel error:', e);
    errorMsg.value = e?.response?.data?.error || e?.message || 'Erro ao cancelar sessão';
  } finally {
    canceling.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  load();
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  stopPolling();
});

watch(() => props.conversationDisplayId, () => {
  // Sprint 5.0.5.3: para polling da conv anterior antes de recarregar
  stopPolling();
  load();
});
</script>

<style scoped>
.combobox-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #1e293b;
  cursor: pointer;
  transition: border-color 120ms, box-shadow 120ms;
  outline: none;
}
.combobox-trigger:hover { border-color: #cbd5e1; }
.combobox-trigger.is-open {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

:global(.dark) .combobox-trigger {
  background: #1e293b;
  border-color: #334155;
  color: #e2e8f0;
}

.bot-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.chevron {
  color: #94a3b8;
  transition: transform 200ms;
  flex-shrink: 0;
}
.chevron.rotated { transform: rotate(180deg); }

.combobox-popover {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  max-height: 320px;
  display: flex;
  flex-direction: column;
  z-index: 50;
  overflow: hidden;
}
:global(.dark) .combobox-popover {
  background: #1e293b;
  border-color: #334155;
}

.combobox-search {
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  outline: none;
  background: transparent;
  color: #1e293b;
}
.combobox-search:focus { background: #f8fafc; }
:global(.dark) .combobox-search { color: #e2e8f0; border-color: #334155; }
:global(.dark) .combobox-search:focus { background: #0f172a; }

.combobox-list {
  flex: 1;
  overflow-y: auto;
  margin: 0;
  padding: 4px 0;
  list-style: none;
}

.combobox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #1e293b;
  transition: background 80ms;
}
.combobox-item:hover { background: #f8fafc; }
.combobox-item.is-selected {
  background: #eef2ff;
  color: #4338ca;
  font-weight: 500;
}
:global(.dark) .combobox-item { color: #e2e8f0; }
:global(.dark) .combobox-item:hover { background: #0f172a; }
:global(.dark) .combobox-item.is-selected { background: #312e81; color: #c7d2fe; }

.bot-name { flex: 1; min-width: 0; }

.status-dots {
  display: flex;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}
.status-dot.inline-flex { display: inline-flex; }
.status-dot.inactive { background: #cbd5e1; }
.status-dot.other-inbox { background: #f59e0b; }

.combobox-empty {
  padding: 16px;
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  font-style: italic;
  list-style: none;
}

.trigger-btn {
  width: 100%;
  padding: 8px 12px;
  background: #e2e8f0;
  color: #94a3b8;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: not-allowed;
  transition: background 120ms;
}
.trigger-btn.has-bot:not(:disabled) {
  background: #6366f1;
  color: white;
  cursor: pointer;
}
.trigger-btn.has-bot:not(:disabled):hover { background: #4f46e5; }
.trigger-btn:disabled { opacity: 0.7; }

.fade-fast-enter-active, .fade-fast-leave-active { transition: opacity 120ms; }
.fade-fast-enter-from, .fade-fast-leave-to { opacity: 0; }

/* Sprint 5.0.5.2: legenda dos status dots no rodape do popover */
.combobox-legend {
  display: flex;
  gap: 12px;
  padding: 8px 12px;
  border-top: 1px solid #f1f5f9;
  font-size: 10px;
  color: #94a3b8;
  background: #fafbfc;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}
:global(.dark) .combobox-legend { background: #0f172a; border-color: #334155; }
</style>
